/**
 * Premium Gate Modal
 *
 * Modal that blocks access to premium features for non-premium users.
 * Provides upsell content and Stripe checkout integration.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CheckIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Stripe price ID from environment variable (injected at build time)
const STRIPE_PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY || process.env.REACT_APP_STRIPE_PRICE_ID_WEEKLY;

export default function PremiumGateModal({
  isOpen,
  onClose,
  title = "Premium Feature",
  description = "Upgrade to unlock this feature and more!",
  features = []
}) {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Don't show if user is premium or modal is closed
  if (!isOpen || userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro') {
    return null;
  }

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);

    try {
      // Ensure we have a price ID from environment
      if (!STRIPE_PRICE_ID) {
        throw new Error('Subscription not configured. Please contact support.');
      }

      const response = await api.post('/subscriptions/create-checkout-session', {
        price_id: STRIPE_PRICE_ID,
        success_url: window.location.origin + '/dashboard?subscription=success',
        cancel_url: window.location.origin + window.location.pathname + '?subscription=cancelled'
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-bg-card border border-border rounded-xl p-8 w-full max-w-lg shadow-2xl">
        {/* Close button - only if onClose is provided */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-bg-secondary transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-text-secondary" />
          </button>
        )}

        {/* Lock Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-4 rounded-full bg-gradient-to-br from-accent-blue/20 to-purple-500/20">
            <LockClosedIcon className="w-10 h-10 text-accent-blue" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-text-primary mb-3">
          {title}
        </h2>

        {/* Description */}
        <p className="text-center text-text-secondary mb-6">
          {description}
        </p>

        {/* Features List */}
        {features.length > 0 && (
          <div className="bg-bg-secondary/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-text-primary">
              <SparklesIcon className="w-4 h-4 text-accent-blue" />
              What you'll get:
            </h3>
            <ul className="space-y-2">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <CheckIcon className="w-4 h-4 text-accent-green flex-shrink-0 mt-0.5" />
                  <span className="text-text-secondary">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pricing */}
        <div className="text-center mb-6">
          <div className="inline-flex items-baseline gap-1">
            <span className="text-3xl font-bold text-text-primary">$30</span>
            <span className="text-text-secondary">/month</span>
          </div>
          <p className="text-xs text-text-secondary mt-1">
            Cancel anytime. No long-term commitment.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        {/* CTA Button */}
        <button
          onClick={handleUpgrade}
          disabled={loading || !STRIPE_PRICE_ID}
          className="w-full py-3 px-6 bg-gradient-to-r from-accent-blue to-purple-500 hover:from-accent-blue/90 hover:to-purple-500/90 text-white font-bold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <SparklesIcon className="w-4 h-4" />
              Upgrade to Premium
            </>
          )}
        </button>

        {/* Security Note */}
        <p className="text-center text-text-secondary text-xs mt-4">
          Secure payment powered by Stripe.
        </p>
      </div>
    </div>
  );

  // Use portal to render modal at document root
  return createPortal(modalContent, document.body);
}
